import { useState } from 'react'
import { ShoppingBag, ChevronDown, ChevronUp } from 'lucide-react'

export interface ReventeData {
  ventesAddTTC: number        // ventes additionnelles TTC (non attribuées à un collab)
  coeffRevente: number        // coefficient fournisseur pour les ventes additionnelles
  tauxTVA: number             // TVA appliquée (défaut 20%)
  commissionPct: number       // % commission pour les ventes additionnelles
  tauxChargesSociales: number
  // legacy (conservés pour rétrocompat)
  coutAchatHT?: number
  prixVenteHT?: number
}

interface Props {
  data: ReventeData
  onChange: (d: ReventeData) => void
  reventeCollabsTTC?: number  // total TTC auto depuis les fiches collaborateurs
}

const e0 = (n: number) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(n)
const e2 = (n: number) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n)

export default function SectionRevente({ data, onChange, reventeCollabsTTC }: Props) {
  const [open, setOpen] = useState(false)
  const set = <K extends keyof ReventeData>(k: K, v: ReventeData[K]) => onChange({ ...data, [k]: v })

  const tauxTVA     = data.tauxTVA ?? 20
  const diviseur    = 1 + tauxTVA / 100
  const coeff       = data.coeffRevente ?? 2.2

  // ── Ventes collaborateurs (reportées depuis les fiches) ──────────────────
  const collabsTTC  = reventeCollabsTTC ?? 0
  const collabsHT   = diviseur > 0 ? collabsTTC / diviseur : 0
  const collabsAchatHT = coeff > 0 ? collabsHT / coeff : 0

  // ── Ventes additionnelles (non attribuées) ────────────────────────────────
  const addTTC  = data.ventesAddTTC ?? 0
  const addHT   = diviseur > 0 ? addTTC / diviseur : 0
  const addAchatHT = coeff > 0 ? addHT / coeff : 0

  // ── Totaux salon ──────────────────────────────────────────────────────────
  const totalVenteTTC = collabsTTC + addTTC
  const totalVenteHT  = collabsHT  + addHT
  const totalAchatHT  = collabsAchatHT + addAchatHT
  const margeBrute    = totalVenteHT - totalAchatHT

  // Primes (globales — sur les ventes additionnelles uniquement, les collabs ont déjà leurs primes individuelles)
  const commissionBrute  = addHT * (data.commissionPct / 100)
  const chargesPatronales = commissionBrute * (data.tauxChargesSociales / 100)
  const coutCommissions  = commissionBrute + chargesPatronales

  // Bénéfice net : marge brute totale − commissions sur ventes additionnelles
  const beneficeNet = margeBrute - coutCommissions

  const hasData = totalVenteTTC > 0

  return (
    <section className="rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-5 py-4"
        style={{ background: open ? '#F0FDF4' : '#F8FAFC' }}>
        <div className="flex items-center gap-2">
          <ShoppingBag className="w-4 h-4" style={{ color: '#16A34A' }} />
          <div className="text-left">
            <div className="font-semibold text-sm" style={{ color: '#16A34A' }}>Revente Produits — Bilan salon</div>
            <div className="text-xs text-gray-500">
              {collabsTTC > 0
                ? `${e0(collabsTTC)} TTC reportés depuis les collaborateurs`
                : 'Total des ventes de produits du salon'}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {hasData && (
            <span className="text-xs font-bold" style={{ color: beneficeNet >= 0 ? '#16A34A' : '#C62828' }}>
              {beneficeNet >= 0 ? '+' : ''}{e0(beneficeNet)} net/mois
            </span>
          )}
          {open ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
        </div>
      </button>

      {open && (
        <div className="bg-white p-5 space-y-5">

          {/* ── Bandeau report collaborateurs ── */}
          {collabsTTC > 0 && (
            <div className="rounded-xl overflow-hidden border border-green-100">
              <div className="px-4 py-2 text-xs font-semibold text-green-700 bg-green-50 flex items-center justify-between">
                <span>📦 Ventes collaborateurs — reportées automatiquement</span>
                <span className="font-black text-green-700">{e0(collabsTTC)} TTC</span>
              </div>
              <div className="divide-y divide-gray-50 text-xs">
                <div className="flex justify-between px-4 py-2">
                  <span className="text-gray-500">÷ TVA ({tauxTVA}%) = CA vente HT</span>
                  <span className="font-mono text-gray-700">{e0(collabsHT)}</span>
                </div>
                <div className="flex justify-between px-4 py-2">
                  <span className="text-gray-500">÷ Coeff {coeff} = Coût d'achat HT estimé</span>
                  <span className="font-mono" style={{ color: '#C62828' }}>− {e0(collabsAchatHT)}</span>
                </div>
              </div>
            </div>
          )}

          {/* ── Paramètres globaux ── */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">TVA appliquée (%)</label>
              <div className="relative">
                <input type="number" min={0} max={30}
                  value={tauxTVA}
                  onChange={e => set('tauxTVA', parseFloat(e.target.value) || 20)}
                  className="w-full pl-3 pr-10 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-200" />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">%</span>
              </div>
              <p className="text-xs text-gray-400 mt-0.5">Taux TVA sur les produits revendus</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Coefficient fournisseur</label>
              <input type="number" min={1} max={10} step={0.1}
                value={coeff}
                onChange={e => set('coeffRevente', parseFloat(e.target.value) || 1)}
                className="w-full pl-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-200" />
              <p className="text-xs text-gray-400 mt-0.5">Ex : 2.2 → achat HT = vente HT ÷ 2.2</p>
            </div>
          </div>

          {/* ── Ventes additionnelles ── */}
          <div className="rounded-xl border border-gray-100 p-4 space-y-3">
            <div className="text-sm font-semibold text-gray-700">Ventes additionnelles (non attribuées à un collaborateur)</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Ventes TTC (€/mois)</label>
                <div className="relative">
                  <input type="number" min={0}
                    value={addTTC || ''}
                    onChange={e => set('ventesAddTTC', parseFloat(e.target.value) || 0)}
                    placeholder="0"
                    className="w-full pl-3 pr-16 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-200" />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">€ TTC</span>
                </div>
                <p className="text-xs text-gray-400 mt-0.5">Ventes de produits non imputées à un collaborateur</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Commission sur ces ventes (%)</label>
                <div className="relative">
                  <input type="number" min={0} max={50}
                    value={data.commissionPct}
                    onChange={e => set('commissionPct', parseFloat(e.target.value) || 0)}
                    className="w-full pl-3 pr-7 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-200" />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">%</span>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Charges sociales sur commission</label>
                <div className="relative">
                  <input type="number" min={0} max={100}
                    value={data.tauxChargesSociales}
                    onChange={e => set('tauxChargesSociales', parseFloat(e.target.value) || 0)}
                    className="w-full pl-3 pr-7 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-200" />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">%</span>
                </div>
                <p className="text-xs text-gray-400 mt-0.5">Charges patronales sur la commission</p>
              </div>
            </div>
          </div>

          {/* ── Tableau bilan salon ── */}
          {hasData && (
            <div className="rounded-xl overflow-hidden border border-gray-100">
              <div className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide bg-gray-50">
                Bilan revente produits — Salon
              </div>
              <div className="divide-y divide-gray-50 text-sm">

                {/* Totaux ventes */}
                <div className="flex justify-between items-center px-4 py-2.5 bg-green-50">
                  <span className="font-semibold text-green-800">CA vente total TTC</span>
                  <span className="font-black font-mono text-green-700">{e0(totalVenteTTC)}</span>
                </div>
                <div className="flex justify-between items-center px-4 py-2 text-xs">
                  <span className="text-gray-500 pl-2">dont collaborateurs</span>
                  <span className="font-mono text-gray-600">{e0(collabsTTC)}</span>
                </div>
                {addTTC > 0 && (
                  <div className="flex justify-between items-center px-4 py-2 text-xs">
                    <span className="text-gray-500 pl-2">dont ventes additionnelles</span>
                    <span className="font-mono text-gray-600">{e0(addTTC)}</span>
                  </div>
                )}
                <div className="flex justify-between items-center px-4 py-2.5">
                  <span className="text-gray-600">CA vente total HT</span>
                  <span className="font-mono text-gray-700">{e0(totalVenteHT)}</span>
                </div>
                <div className="flex justify-between items-center px-4 py-2.5">
                  <span className="text-gray-600">− Coût d'achat total HT</span>
                  <span className="font-mono" style={{ color: '#C62828' }}>− {e0(totalAchatHT)}</span>
                </div>
                <div className="flex justify-between items-center px-4 py-2.5 font-semibold bg-blue-50/40">
                  <span className="text-gray-800">= Marge brute HT</span>
                  <span className="font-mono" style={{ color: '#2563EB' }}>{e0(margeBrute)}</span>
                </div>

                {/* Commissions sur ventes additionnelles */}
                {addTTC > 0 && (
                  <>
                    <div className="flex justify-between items-center px-4 py-2.5">
                      <span className="text-gray-600">− Commission ventes add. ({data.commissionPct}% × HT)</span>
                      <span className="font-mono" style={{ color: '#E65100' }}>− {e0(commissionBrute)}</span>
                    </div>
                    <div className="flex justify-between items-center px-4 py-2.5">
                      <span className="text-gray-600">− Charges patronales ({data.tauxChargesSociales}%)</span>
                      <span className="font-mono" style={{ color: '#E65100' }}>− {e0(chargesPatronales)}</span>
                    </div>
                  </>
                )}

                {/* Résultat final */}
                <div className="flex justify-between items-center px-4 py-4"
                  style={{ background: beneficeNet >= 0 ? '#F0FDF4' : '#FFF5F5', borderTop: '2px solid ' + (beneficeNet >= 0 ? '#86EFAC' : '#FCA5A5') }}>
                  <div>
                    <div className="font-bold text-sm" style={{ color: beneficeNet >= 0 ? '#16A34A' : '#C62828' }}>= Bénéfice net revente pour le salon</div>
                    <div className="text-xs text-gray-400">Après achat et commissions</div>
                  </div>
                  <div className="text-right">
                    <div className="text-xl font-black font-mono" style={{ color: beneficeNet >= 0 ? '#16A34A' : '#C62828' }}>
                      {beneficeNet >= 0 ? '+' : '−'}{e0(Math.abs(beneficeNet))}
                    </div>
                    <div className="text-xs text-gray-400">HT / mois</div>
                  </div>
                </div>
              </div>

              {/* KPIs synthèse */}
              <div className="grid grid-cols-3 divide-x divide-gray-100 border-t border-gray-100">
                <div className="p-3 text-center">
                  <div className="text-xs text-gray-500 mb-0.5">Marge brute %</div>
                  <div className="font-bold text-sm font-mono" style={{ color: '#2563EB' }}>
                    {totalVenteHT > 0 ? `${Math.round((margeBrute / totalVenteHT) * 100)}%` : '—'}
                  </div>
                </div>
                <div className="p-3 text-center">
                  <div className="text-xs text-gray-500 mb-0.5">Bénéfice net</div>
                  <div className="font-bold text-sm font-mono" style={{ color: beneficeNet >= 0 ? '#16A34A' : '#C62828' }}>
                    {beneficeNet >= 0 ? '+' : ''}{e0(beneficeNet)}
                  </div>
                  <div className="text-xs text-gray-400">/mois</div>
                </div>
                <div className="p-3 text-center">
                  <div className="text-xs text-gray-500 mb-0.5">Projection annuelle</div>
                  <div className="font-bold text-sm font-mono" style={{ color: beneficeNet >= 0 ? '#16A34A' : '#C62828' }}>
                    {beneficeNet >= 0 ? '+' : ''}{e0(beneficeNet * 12)}
                  </div>
                  <div className="text-xs text-gray-400">/an</div>
                </div>
              </div>

              {/* Coût-minute revente */}
              {totalVenteTTC > 0 && (
                <div className="px-4 py-2 border-t border-gray-100 text-xs text-gray-500 bg-gray-50">
                  💡 <strong>{e2(beneficeNet > 0 ? beneficeNet / (totalVenteTTC / 100) : 0)}%</strong> de marge nette sur le TTC encaissé
                </div>
              )}
            </div>
          )}

          {!hasData && (
            <div className="text-center py-6 text-gray-400 text-sm">
              Saisissez les ventes dans les fiches collaborateurs pour voir le bilan
            </div>
          )}
        </div>
      )}
    </section>
  )
}
